const GROUP_DEFINITIONS = Object.freeze({
  poster: { ids: ['1', '171', '268'], labels: { '1': 'Enhanced Matte Poster (in)', '171': 'Premium Luster Poster (in)', '268': 'Enhanced Matte Poster (cm)' }, noun: 'poster' },
  framed: { ids: ['2', '172', '304', '366', '614'], labels: { '2': 'Enhanced Matte Framed Poster (in)', '172': 'Premium Luster Framed Poster (in)', '304': 'Framed Poster (cm)', '366': 'Matted Framed Poster (cm)', '614': 'Framed Canvas (in)' }, noun: 'framed print' },
  hanging: { ids: ['640'], labels: { '640': 'Enhanced Matte Poster With Hanger (in)' }, noun: 'hanging poster' }
});

class VoicewavePosterConfigurator extends HTMLElement {
  async connectedCallback() {
    this.groupKey = GROUP_DEFINITIONS[this.dataset.group] ? this.dataset.group : 'poster';
    this.definition = GROUP_DEFINITIONS[this.groupKey];
    this.typeSelect = this.querySelector('[data-vpf-type]');
    this.sizeSelect = this.querySelector('[data-vpf-size]');
    this.frameSelect = this.querySelector('[data-vpf-frame]');
    this.status = this.querySelector('[data-vpf-status]');
    this.action = this.querySelector('[data-vpf-action]');
    this.outputs = { type: this.querySelector('[data-vpf-output="type"]'), size: this.querySelector('[data-vpf-output="size"]'), frame: this.querySelector('[data-vpf-output="frame"]'), price: this.querySelector('[data-vpf-output="price"]') };
    this.typeSelect?.addEventListener('change', () => this.renderSizes());
    this.sizeSelect?.addEventListener('change', () => this.renderFrames());
    this.frameSelect?.addEventListener('change', () => this.publish());
    await this.load();
  }

  async load() {
    try {
      const response = await fetch('/apps/voicewave/catalog', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Catalog unavailable');
      const payload = await response.json();
      const ids = new Set(this.definition.ids);
      this.items = (Array.isArray(payload.products) ? payload.products : []).filter(item => ids.has(String(item.catalogProductId)) && item.shopify?.published !== false);
      const availableIds = this.definition.ids.filter(id => this.items.some(item => String(item.catalogProductId) === id));
      if (!availableIds.length) throw new Error('Product group unavailable');
      this.typeSelect.replaceChildren(new Option('Select product type', ''));
      for (const id of availableIds) this.typeSelect.add(new Option(this.labelForId(id), id));
      this.typeSelect.disabled = false;
      this.status.textContent = `Choose your ${this.definition.noun} type to see live available sizes.`;
      this.typeSelect.value = availableIds[0];
      this.renderSizes();
    } catch (_) {
      this.status.textContent = 'Live product options are temporarily unavailable.';
    }
  }

  labelForId(id) { return this.definition.labels[String(id)] || `Product ${id}`; }
  selectedItems() { return this.items?.filter(item => String(item.catalogProductId) === this.typeSelect.value) || []; }
  fill(select, values, placeholder) {
    const unique = [...new Set(values.filter(Boolean))];
    select.replaceChildren(new Option(placeholder, ''));
    for (const value of unique) select.add(new Option(value, value));
    select.disabled = unique.length === 0;
  }
  renderSizes() {
    const sizes = [...new Set(this.selectedItems().map(item => item.size || item.attributes?.Size).filter(Boolean))];
    this.fill(this.sizeSelect, sizes, 'Select size');
    this.fill(this.frameSelect, [], 'Select a size first');
    if (this.outputs.type) this.outputs.type.textContent = this.typeSelect.value ? this.labelForId(this.typeSelect.value) : '—';
    if (this.outputs.size) this.outputs.size.textContent = '—';
    if (this.outputs.frame) this.outputs.frame.textContent = '—';
    if (this.outputs.price) this.outputs.price.textContent = '—';
    this.lockAction();
    if (sizes.length) { this.sizeSelect.value = sizes[0]; this.renderFrames(); }
  }
  renderFrames() {
    const matches = this.selectedItems().filter(item => (item.size || item.attributes?.Size) === this.sizeSelect.value);
    const finishes = [...new Set(matches.map(item => item.attributes?.Frame || item.attributes?.FrameColor || item.attributes?.Color || item.attributes?.Finish || item.attributes?.WallHangerColor).filter(Boolean))];
    if (finishes.length) { this.fill(this.frameSelect, finishes, 'Select finish'); this.frameSelect.value = finishes[0]; }
    else { this.fill(this.frameSelect, ['Standard finish'], 'Standard finish'); this.frameSelect.value = 'Standard finish'; }
    if (this.outputs.size) this.outputs.size.textContent = this.sizeSelect.value || '—';
    this.publish();
  }
  exact() {
    const val = this.frameSelect.value;
    const candidates = this.selectedItems().filter(item => (item.size || item.attributes?.Size) === this.sizeSelect.value);
    return candidates.find(item => !val || val === 'Standard finish' || [item.attributes?.Frame, item.attributes?.FrameColor, item.attributes?.Color, item.attributes?.Finish, item.attributes?.WallHangerColor].includes(val)) || candidates[0];
  }
  lockAction() { this.action.removeAttribute('href'); this.action.setAttribute('aria-disabled', 'true'); }
  publish() {
    if (this.outputs.frame) this.outputs.frame.textContent = this.frameSelect.value || '—';
    const exact = this.exact();
    if (!exact?.productUid) { this.lockAction(); return; }
    if (this.outputs.price && exact.shopify?.retailPrice) this.outputs.price.textContent = `$${exact.shopify.retailPrice}`;
    const url = new URL(this.dataset.customizerUrl || '/pages/create-keepsake', location.origin);
    url.searchParams.set('family', exact.familyKey);
    url.searchParams.set('lockFamily', '1');
    url.searchParams.set('productUid', exact.productUid);
    this.action.href = `${url.pathname}${url.search}`;
    this.action.removeAttribute('aria-disabled');
    this.status.textContent = `This exact ${this.definition.noun} configuration is ready to personalize.`;
  }
}
if (!customElements.get('voicewave-poster-configurator')) customElements.define('voicewave-poster-configurator', VoicewavePosterConfigurator);

document.addEventListener('click', event => {
  const cardBtn = event.target.closest('[data-vpf-select-product]');
  if (cardBtn) {
    const configurator = document.querySelector('voicewave-poster-configurator');
    const select = configurator?.querySelector('[data-vpf-type]');
    if (select && !select.disabled && [...select.options].some(option => option.value === cardBtn.dataset.vpfSelectProduct)) {
      select.value = cardBtn.dataset.vpfSelectProduct || '';
      select.dispatchEvent(new Event('change', { bubbles: true }));
      document.getElementById('poster-configurator')?.scrollIntoView({ behavior: 'smooth' });
    }
  }
  const tab = event.target.closest('[data-vpf-gallery-tab]');
  if (!tab) return;
  const root = tab.closest('[data-vpf-gallery]');
  const image = root?.querySelector('[data-vpf-gallery-image]');
  if (!image || !tab.dataset.imageUrl) return;
  for (const item of root.querySelectorAll('[data-vpf-gallery-tab]')) item.setAttribute('aria-selected', String(item === tab));
  image.removeAttribute('srcset');
  image.src = tab.dataset.imageUrl;
  image.alt = tab.dataset.imageAlt || '';
});
